(() => {
  const money = value => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format((Number(value) || 0) / 100);
    } catch (_) {
      return `₹${Math.round((Number(value) || 0) / 100)}`;
    }
  };

  const escapeHTML = value => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  async function getCart() {
    try {
      const r = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) return { items: [] };
      return await r.json();
    } catch (_) {
      return { items: [] };
    }
  }

  function profileSignals() {
    try {
      if (window.BowkartAIProfile && window.BowkartAIProfile.get) {
        return window.BowkartAIProfile.get() || {};
      }
      return JSON.parse(localStorage.getItem('bk_ai_personalization_v2')) || {};
    } catch (_) {
      return {};
    }
  }

  function cartForAPI(cart) {
    return (cart.items || []).map(item => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      handle: item.handle,
      title: item.product_title || item.title,
      variant_title: item.variant_title || '',
      product_type: item.product_type || '',
      quantity: item.quantity,
      price: item.final_price || item.price
    }));
  }

  // Cards are rendered by the theme itself (sections/bk-product-card.liquid → Kalles card design 9),
  // so "For you" rows look exactly like every other product card on the site.
  const cardCache = new Map();
  async function fetchCard(handle) {
    if (cardCache.has(handle)) return cardCache.get(handle);
    const job = (async () => {
      try {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const r = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=bk-product-card`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!r.ok) return null;
        const html = await r.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const src = doc.querySelector('.bk-card-src');
        return src && src.firstElementChild ? src.innerHTML.trim() : null;
      } catch (_) {
        return null;
      }
    })();
    cardCache.set(handle, job);
    return job;
  }

  function productCard(cardHTML, handle, row, position) {
    return `
      <div class="bk-ai-card" data-bk-ai-product
        data-handle="${escapeHTML(handle)}"
        data-section-key="${escapeHTML(row.key)}"
        data-section-title="${escapeHTML(row.title)}"
        data-position="${position}">${cardHTML}</div>`;
  }

  function analyticsContext(section) {
    return section.__bkAiAnalytics || {
      eventEndpoint: '',
      profile: {},
      sessionId: ''
    };
  }

  function sendEvent(section, eventName, card) {
    const ctx = analyticsContext(section);
    if (!ctx.eventEndpoint) return;

    const payload = {
      event: eventName,
      session_id: ctx.sessionId || '',
      section_key: card ? card.dataset.sectionKey || '' : '',
      section_title: card ? card.dataset.sectionTitle || '' : '',
      handle: card ? card.dataset.handle || '' : '',
      position: card ? Number(card.dataset.position || 0) : 0,
      visitor_type: ctx.profile.visitor_type || '',
      likely_age: ctx.profile.likely_age || '',
      preferred_category: ctx.profile.preferred_category || ''
    };

    try {
      fetch(ctx.eventEndpoint, {
        method: 'POST',
        mode: 'cors',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(() => {});
    } catch (_) {}
  }

  async function renderSection(row, host, parentSection) {
    const handles = [...new Set((row.handles || []).filter(Boolean))];
    if (!handles.length) return;

    const cards = await Promise.all(handles.map(h => fetchCard(h).then(html => ({ h, html }))));
    const ready = cards.filter(c => c.html);
    if (!ready.length) return;

    const el = document.createElement('section');
    el.className = `bk-ai-row bk-ai-row--${escapeHTML(row.key)}`;
    el.dataset.bkAiRow = row.key;
    el.dataset.bkAiRowTitle = row.title;

    el.innerHTML = `
      <div class="bk-head">
        <div>
          <span class="bk-eyebrow">For you</span>
          <h2 class="bk-h2">${escapeHTML(row.title)}</h2>
        </div>
      </div>
      <div class="bk-ai-rail hdt-collection-products hdt-collection-has-pr9 hdt-pr-border_on_item hdt-ratio--portrait">
        ${ready.map((c, i) => productCard(c.html, c.h, row, i + 1)).join('')}
      </div>
    `;

    host.appendChild(el);

    const firstCard = el.querySelector('[data-bk-ai-product]');
    if (firstCard) sendEvent(parentSection, 'section_impression', firstCard);
  }

  async function quickAdd(button, parentSection) {
    const variantId = Number(button.dataset.bkAiAdd);
    if (!variantId) return;

    const card = button.closest('[data-bk-ai-product]');
    const original = button.textContent;

    sendEvent(parentSection, 'quick_add', card);

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!r.ok) throw new Error('Add failed');

      button.textContent = 'Added';
      sendEvent(parentSection, 'quick_add_success', card);

      document.dispatchEvent(new CustomEvent('bowkart:cart:changed'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 1600);
    } catch (_) {
      button.textContent = 'Try again';
      button.disabled = false;
      sendEvent(parentSection, 'quick_add_error', card);
    }
  }

  async function init(section) {
    if (section.dataset.bkAiReady === '1') return;
    section.dataset.bkAiReady = '1';

    const endpoint = section.dataset.endpoint;
    const eventEndpoint = section.dataset.eventEndpoint || '';
    const limit = Number(section.dataset.limit || 4);
    const status = section.querySelector('[data-bk-ai-status]');
    const rows = section.querySelector('[data-bk-ai-rows]');

    if (!endpoint || !rows) return;

    const signals = profileSignals();
    const cart = await getCart();

    const payload = {
      recent_views: signals.recent_views || [],
      collections_viewed: signals.collections_viewed || [],
      search_terms: signals.search_terms || [],
      cart_items: cartForAPI(cart),
      limit
    };

    try {
      const r = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await r.json();
      if (!data || !data.ok || !Array.isArray(data.sections)) {
        throw new Error('No personalization');
      }

      section.__bkAiAnalytics = {
        eventEndpoint,
        profile: data.profile || {},
        sessionId: signals.session_id || ''
      };

      rows.innerHTML = '';

      // fetch every card once (in parallel), then build rows in order
      await Promise.all(data.sections.flatMap(r => (r.handles || []).filter(Boolean).map(fetchCard)));

      // each product appears only once across the "For you" rows (Recently viewed keeps its own list)
      const seen = new Set();
      for (const row of data.sections) {
        const isRecent = /recent/i.test(`${row.key || ''} ${row.title || ''}`);
        const handles = [...new Set((row.handles || []).filter(Boolean))];
        const unique = isRecent ? handles : handles.filter(h => !seen.has(h));
        if (!isRecent) unique.forEach(h => seen.add(h));
        if (unique.length < 2) continue;
        await renderSection({ ...row, handles: unique }, rows, section);
      }

      if (!rows.children.length) throw new Error('No rows');

      status.hidden = true;
      rows.hidden = false;
    } catch (_) {
      // No personalised rows available: hide the block instead of showing placeholder text
      status.hidden = true;
      section.hidden = true;
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-bk-ai-add]');
    if (button) {
      const section = button.closest('[data-bk-ai-home]');
      if (section) quickAdd(button, section);
      return;
    }

    const card = event.target.closest('[data-bk-ai-product]');
    const section = card && card.closest('[data-bk-ai-home]');
    if (!card || !section) return;
    if (event.target.closest('a[href*="/products/"]')) {
      sendEvent(section, 'product_click', card);
    } else if (event.target.closest('button, [role="button"]') && !event.target.closest('.hdt-card-product__btn-wishlist, [class*="wishlist"]')) {
      sendEvent(section, 'quick_add', card);
    }
  });

  // Bowkart: start each personalised block only when it comes near the screen,
  // so its cart and API requests do not compete with the first view of the page.
  function boot() {
    const els = Array.from(document.querySelectorAll('[data-bk-ai-home]:not([data-bk-booted])'));
    const start = el => { el.setAttribute('data-bk-booted', ''); init(el); };
    if (!('IntersectionObserver' in window)) { els.forEach(start); return; }
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { io.unobserve(entry.target); start(entry.target); }
      });
    }, { rootMargin: '600px 0px' });
    els.forEach(el => io.observe(el));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();